import * as React from "react";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";
import styled from "styled-components";

import {
  FilterParams,
  PaginatedParams
} from "src/library/utils";
import Table, { TableRefProps } from "src/core/dashboard/components/widgets/table/Table";
import { OrderActionsList } from "src/core/orders/components/OrderActionsList";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import {
  ListOrdersParams,
  ordersApi,
  OrdersResponse,
  IOrder,
  OrderOrigin
} from "src/core/orders/api/orders.api";
import { setOrdersWithErrors } from "src/core/orders/store/orders.actions";
import { defaultPagination } from "src/core/orders/store/orders.reducer";
import type { IStore } from "src/core/shared/store";
import { PageProps } from "src/pages/Router/routes";
import { adminOrderTableNewColumns } from "src/core/shared/components/tables/adminOrderTableColumns";
import { CreateOrderContext } from "src/core/shared/components/HeaderContext";
import { useOrder } from "src/core/orders/hooks";
import Grid from "src/library/components/Hedron";

const AccountManagerOrders: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const { history, terminology } = props;
  const dispatch = useDispatch();
  const [ ordersResponse, getOrders ] = ordersApi<OrdersResponse, ListOrdersParams>("LIST_ORDERS");
  const ordersData = ordersResponse.data;
  const isLoading = ordersResponse.pending;
  const [ { isCreatingOrder }, { createOrder } ] = useOrder();

  const {
    orderStatuses, role, erp, installedModules
  } = useMappedState((store: IStore) => ({
    user: store.global.user,
    orderStatuses: store.global.settings ? store.global.settings.orderStatuses : [],
    role: store.global.role,
    erp: store.global.settings && store.global.settings.erp.name,
    installedModules: store.global.settings?.installedModules || []
  }));

  const TableRef = React.useRef<TableRefProps>(null);

  const reload = React.useCallback(
    () => {
      if (TableRef.current) {
        TableRef.current.reloadTable();
      }
    },
    [ TableRef ]
  );

  React.useEffect(() => {
    reload();
  }, [ reload ]);

  const onFetchData = React.useCallback((parameters: { filters: FilterParams<IOrder>, pagination: PaginatedParams<IOrder> }) => {
    const { orderStatus, ...filters } = parameters.filters;

    const params = {
      pagination: parameters.pagination,
      filters: {
        ...filters,
        orderStatusId: orderStatus ? parseInt(orderStatus.id, 10) : undefined
      }
    };

    getOrders<ListOrdersParams>(params);
  }, []);

  React.useEffect(() => {
    if (ordersResponse.data) {
      dispatch(setOrdersWithErrors(ordersResponse.data.allOrdersWithErrors));
    }
  }, [ ordersResponse, dispatch ]);

  const columns = React.useMemo(() => adminOrderTableNewColumns({
    installedModules,
    terminology,
    orderStatuses,
    pricesVisible: true,
    actionButton: info => {
      if (erp && role) {
        return (
          <OrderActionsList
            erp={erp}
            terminology={terminology}
            role={role}
            order={info.row.original}
          />
        );
      }

      return null;
    }
  }), [
    erp,
    installedModules,
    orderStatuses,
    role,
    terminology
  ]);

  return (
    <Layout {...props}>
      <MainContentWrapper fill>
        <OrderCreationWrapper>
          <CreateOrderContext
            requireDropSelection
            direction="left"
            key={0}
            drop={null}
            loading={isCreatingOrder}
            createDraftOrder={(deliveryDate, options) => createOrder({
              date: deliveryDate,
              origin: OrderOrigin.Manual,
              dropOverride: options?.drop,
              orderVariantId: options?.orderVariant?.id
            })}
          />
        </OrderCreationWrapper>

        <Table
          tableOf={terminology.order.plural.text}
          className="large"
          data={ordersData ? ordersData.orders.result : []}
          onFetchData={onFetchData}
          defaultPagination={defaultPagination}
          pageCount={ordersData ? ordersData.orders.pages : 0}
          rowCount={ordersData ? ordersData.orders.count : 0}
          error={ordersResponse.error}
          mainAction={row => history.push(`/orders/${row.original.id}`)}
          defaultVisibility={{
            id: false,
            invoiceNumber: false
          }}
          ref={TableRef}
          loading={isLoading}
          columns={columns}
        />

      </MainContentWrapper>
    </Layout>
  );
};

const OrderCreationWrapper = styled(Grid.Bounds)`
  position: relative;
  padding: ${({ theme }) => theme.spacing.padding.md} 0;
`;

export default AccountManagerOrders;
